import React from "react";
import { motion, MotionConfig } from "framer-motion";
import { FONT } from "@/lib/tokens";

/* ──────────────────────────────────────────────────────────────
   La fenetre d'exemple : a quoi ressemble un systeme, poste par
   poste. Elle avait sa propre section ("Qu'est-ce qu'un Systeme
   TANGIG ?"), la troisieme definition d'affilee sur l'accueil.
   Elle vit desormais DANS "Comment ca marche", ou elle sert de
   preuve a la premiere carte : voila ce qu'une fiche annonce.
   Ce composant ne rend donc plus de <section> ni d'en-tete, juste
   la scene et son CSS.
   ────────────────────────────────────────────────────────────── */

/* ── Design tokens (from handoff: Sales Operating System) ───── */
const INK      = "#1D1D1F";
const GREY_1   = "#6E6E73";
const LINE_2   = "#E8E8ED";

/* Apple "magnetic" signature easing used across this component */
const SOM_EASE = [0.28, 0.11, 0.32, 1];

const CSS = `
  /* ── Stage: hosts the window + the floating card that overhangs it ── */
  .tg-som-stage {
    position: relative;
    font-family: ${FONT};
    width: min(1180px, 100%);
    margin: 0 auto;
  }

  /* ── The big window ── */
  /* Plus de carte blanche : le contenu respire directement sur le fond
     de la page. On garde la meme grille et les memes rythmes verticaux. */
  .tg-som-window {
    position: relative;
    background: transparent;
    border: 0;
    border-radius: 0;
    box-shadow: none;
    padding: 24px 0 40px;
    box-sizing: border-box;
    overflow: visible;
  }

  .tg-som-hero {
    position: relative;
    max-width: 900px;
    margin: 0 auto;
    text-align: center;
  }

  .tg-som-eyebrow {
    font-size: clamp(18px, 2vw, 19.5px);
    font-weight: 600;
    letter-spacing: -0.01em;
    text-transform: none;
    /* Couleur unique des labels de l'accueil : un noir adouci, un cran plus
       clair que le #1D1D1F des titres. Assez proche pour rester sobre, assez
       distinct pour que le label ne se confonde pas avec le titre du dessous. */
    color: #3A3A3C;
    margin: 0 0 22px;
  }

  .tg-som-title {
    font-family: 'Inter', sans-serif;
    font-weight: 700;
    font-size: clamp(34px, 4vw, 54px);
    line-height: 1.04;
    letter-spacing: -0.028em;
    color: ${INK};
    margin: 0 0 20px;
  }

  .tg-som-sub {
    font-size: clamp(17px, 1.4vw, 20px);
    line-height: 1.4;
    letter-spacing: -0.01em;
    color: ${GREY_1};
    font-weight: 400;
    max-width: 560px;
    margin: 0 auto 64px;
  }

  .tg-som-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 72px;
    max-width: 720px;
    margin: 0 auto;
    text-align: left;
  }

  .tg-som-row {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 19px 2px;
    border-bottom: 1px solid ${LINE_2};
  }
  .tg-som-row.no-border {
    border-bottom: none;
  }

  .tg-som-row-icon {
    flex: 0 0 auto;
    width: 26px;
    height: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${GREY_1};
  }

  .tg-som-row-label {
    font-size: 17px;
    font-weight: 500;
    letter-spacing: -0.01em;
    color: ${INK};
  }

  /* ── Floating integrations visual (image only, magnetic) ── */
  .tg-som-integrations-wrap {
    position: relative;
    z-index: 5;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    margin: 84px auto 0;
  }

  .tg-som-integrations-caption {
    font-family: 'Inter', sans-serif;
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.012em;
    color: ${INK};
    text-align: center;
    margin: 0 0 22px;
  }

  .tg-som-integrations-img {
    display: block;
    width: 460px;
    max-width: 100%;
    height: auto;
    border-radius: 28px;
    box-shadow: 0 2px 6px rgba(0,0,0,0.08), 0 28px 64px -22px rgba(0,0,0,0.42);
    transition: transform 0.5s cubic-bezier(0.28,0.11,0.32,1), box-shadow 0.5s cubic-bezier(0.28,0.11,0.32,1);
  }
  .tg-som-integrations-img:hover {
    transform: translateY(-6px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.12), 0 46px 100px -16px rgba(0,0,0,0.60);
  }

  /* ── Mobile ── */
  @media (max-width: 760px) {
    .tg-som-window { padding: 8px 0 32px; }
    .tg-som-grid { grid-template-columns: 1fr; column-gap: 0; }
    .tg-som-row { border-bottom: 1px solid ${LINE_2} !important; }
    .tg-som-row.last-col { border-bottom: none !important; }

    .tg-som-integrations-wrap { margin-top: 56px; }
    .tg-som-integrations-caption { font-size: 15px; margin-bottom: 18px; }
    .tg-som-integrations-img { width: 100%; max-width: 420px; border-radius: 24px; }
  }
`;

/* ── Feature-grid icons (fine line, matches Lucide 2px round-cap style) ── */
const iconProps = {
  width: 24, height: 24, viewBox: "0 0 24 24", fill: "none",
  stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round",
};

function IconCRM() {
  return (
    <svg {...iconProps}>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 19.5c0-3 2.6-5 5.5-5s5.5 2 5.5 5" />
      <path d="M16 7.5a2.6 2.6 0 0 1 0 5" />
      <path d="M17.5 14.6c2 .5 3.5 2.1 3.5 4.4" />
    </svg>
  );
}
function IconAgent() {
  return (
    <svg {...iconProps}>
      <path d="M12 3.5l1.9 4.4 4.6 1.6-4.6 1.6L12 15.5l-1.9-4.4L5.5 9.5l4.6-1.6z" />
      <path d="M18 15.5l.8 1.9 1.9.8-1.9.8-.8 1.9-.8-1.9-1.9-.8 1.9-.8z" />
    </svg>
  );
}
function IconProspection() {
  return (
    <svg {...iconProps}>
      <circle cx="12" cy="12" r="8.2" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="12" cy="12" r="0.4" fill="currentColor" />
    </svg>
  );
}
function IconQualification() {
  return (
    <svg {...iconProps}>
      <path d="M4 5.5h16l-6.2 7.4v5.1L10.2 20v-7.1z" />
    </svg>
  );
}
function IconReporting() {
  return (
    <svg {...iconProps}>
      <path d="M4 20V4" />
      <path d="M4 20h16" />
      <rect x="7.5" y="12" width="2.6" height="5" />
      <rect x="12.2" y="8.5" width="2.6" height="8.5" />
      <rect x="16.9" y="5.5" width="2.6" height="11.5" />
    </svg>
  );
}
function IconDocumentation() {
  return (
    <svg {...iconProps}>
      <path d="M6 3.5h8l4 4V20a.5.5 0 0 1-.5.5h-11A.5.5 0 0 1 6 20z" />
      <path d="M13.5 3.7V8h4.3" />
      <path d="M9 12.5h6" />
      <path d="M9 15.8h6" />
    </svg>
  );
}
function IconPayment() {
  return (
    <svg {...iconProps}>
      <rect x="3.5" y="6" width="17" height="12" rx="2.4" />
      <path d="M3.5 10h17" />
      <path d="M7 14.5h4" />
    </svg>
  );
}
function IconSupport() {
  return (
    <svg {...iconProps}>
      <path d="M5 17.5c-1-1.3-1.6-2.9-1.6-4.7C3.4 8.2 7.2 4.8 12 4.8s8.6 3.4 8.6 8c0 4.5-3.8 8-8.6 8c-1.3 0-2.5-.25-3.6-.7L4.5 21z" />
      <path d="M8.5 12.5h.01M12 12.5h.01M15.5 12.5h.01" />
    </svg>
  );
}

const FEATURES = [
  { name: "CRM intégré",    Icon: IconCRM },
  { name: "Agent IA",       Icon: IconAgent },
  { name: "Prospection",    Icon: IconProspection },
  { name: "Qualification",  Icon: IconQualification },
  { name: "Reporting",      Icon: IconReporting },
  { name: "Documentation",  Icon: IconDocumentation },
  { name: "Paiement",       Icon: IconPayment },
  { name: "Support",        Icon: IconSupport },
];


export default function SystemWindow() {
  return (
    <MotionConfig reducedMotion="user">
      <style>{CSS}</style>
      <div className="tg-som-stage">
        {/* THE BIG WINDOW — rises first */}
        <motion.div
          className="tg-som-window"
          initial={{ opacity: 0, y: 28, scale: 0.988 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.9, ease: SOM_EASE }}
        >
          <div className="tg-som-hero">
            {/* "Exemple", et non "Le systeme" : cette fenetre illustre le
                concept, elle n'annonce pas un produit du catalogue. Le
                conditionnel du sous-titre le confirme, pour qu'aucun
                visiteur n'aille chercher ce systeme dans Les Systemes. */}
            <div className="tg-som-eyebrow">Exemple</div>
            <h3 className="tg-som-title">Système commercial</h3>
            <p className="tg-som-sub">Ce qu&apos;un tel système réunirait, poste par poste.</p>

            <div className="tg-som-grid">
              {FEATURES.map(({ name, Icon }, i) => {
                const isLastRow = i >= FEATURES.length - 2;
                return (
                  <div key={name} className={`tg-som-row${isLastRow ? " no-border" : ""}`}>
                    <span className="tg-som-row-icon"><Icon /></span>
                    <span className="tg-som-row-label">{name}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* INTEGRATIONS VISUAL — sits low inside the window, centred */}
          <motion.div
            className="tg-som-integrations-wrap"
            initial={{ opacity: 0, y: 28, scale: 0.96 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.9, ease: SOM_EASE, delay: 0.5 }}
          >
            <p className="tg-som-integrations-caption">S&apos;intègre à votre écosystème</p>
            <img
              className="tg-som-integrations-img"
              src="/images/tangig-ecosysteme.webp"
              alt="Un système TANGIG connecté à ChatGPT, Claude, Notion, Slack, Stripe et HubSpot"
              loading="lazy"
              decoding="async"
            />
          </motion.div>
        </motion.div>
      </div>
    </MotionConfig>
  );
}
